<template lang="pug">
v-flex
  v-card.console-card.pb-4(height="100%")
    v-layout(row wrap)
      v-flex(:class="limitComponentClasses")
        .headline.pb-3.font-weight-bold TEXTING CONSOLE
        .pb-1 Texting limits reset each month automatically.
        text-limits-widget.mb-4(
          :limit="textsLimit"
          :remaining="remainingTexts"
          textType="Text"
          :disabled="!isCompleted"
        )
          template(slot="icon")
            v-avatar(color="grey" size="35")
              v-img.logo(src="/static/img/cellphone.svg" contain)
        text-limits-widget(
          :limit="videoTextsLimit"
          :remaining="remainingVideoTexts"
          textType="Video Text"
          :disabled="!isCompleted"
        )
          template(slot="icon")
            v-avatar(color="grey" size="35")
              v-img.logo(src="/static/img/cellphone-play.svg" contain)
      v-flex(:class="stateComponentClasses")
        .pb-5
          .headline.pb-3.font-weight-bold(v-html="title")
          div(v-if="customNumberPurchasedURL").purcharsed-custom-number.mb-2
            p.pa-2.ma-0.font-weight-bold.purcharsed-custom-number--main Congratulations on securing your custom number! 
              span.font-weight-regular.purcharsed-custom-number--regular Your next step is to unlock texting by registering with the US carriers so they can verify your identity. Click the button below to start your registration.
          .text-component.subheading.font-weight-bold(v-if="subtitle") {{ subtitle }}
          v-stepper.stepper-container(v-if="has10dlcRegister" :value="currentStep" alt-labels) 
            v-stepper-header.stepper-header
              template(v-for="(step, index) in steps") 
                v-stepper-step.step(
                  :key="index"
                  :complete="currentStep >= index + 1"
                  step=""
                  :complete-icon="completeIcon(index + 1)"
                  :color="stepColor(index + 1)"
                )
                  .caption Step {{ index + 1 }}
                  .font-weight-bold.subheading.colapse {{ step.name }}
                  .caption(:style="stepStyle(index + 1)") {{ stepState(index + 1) }}
                v-divider.divider(v-if="index + 1 < steps.length")
            v-stepper-items 
              v-stepper-content.content(
                v-for="(step, index) in steps"
                :key="index"
                :step="index + 1"
              )
                v-btn.content-button(
                  v-if="!stepHasError && step.pendingCta"
                  :style="buttonStyle"
                  @click="actionByKeyword(step.pendingAction)"
                ) {{ step.pendingCta }}
                v-btn.content-button(
                  v-if="stepHasFailedError && step.errorCta"
                  :style="buttonStyle"
                  @click="actionByKeyword(step.errorAction)"
                ) {{ step.errorCta + showRetryAttempts }}
                .pl-2.text-component.campaign-warning(
                  v-if="campaignFailure && !stepHasTechError"
                ) {{ campaignRetryWarning }}
                .text-component.subheading.error-message(
                  v-if="errorActionNote"
                ) {{ errorActionNote }} 
                .text-component.subheading.error-message(
                  v-if="stepHasTechError || stepHasBlockedError"
                ) 
                  span There was a registration error
                  span(v-html="errorMessage")
                  span Please&nbsp;
                  a(:href="errorMailString" target="_blank") contact support&nbsp;
                  span(v-if="afterSupportMessage" v-html="afterSupportMessage")
                  span(v-else) for assistance.
                .text-component.subheading(v-if="step.copy && hasBulkTextsAccess" v-html="step.copyBulkTexts")
                .text-component.subheading(v-else-if="step.copy" v-html="step.copy")
          v-btn.mx-0.text-none(v-else color="primary" @click="actionByKeyword('ein')") Click here to unlock texting
        div
          .text-component.subheading.font-weight-bold(v-if="note") {{ note }}
          .mt-3
            //- Replace with link when it's ready
            a(
              href="https://lonewolf.my.site.com/s/article/LD-FAQ-Texting-brand-registration-changes-June-2023-LionDesk"
              target="_blank"
            ) Learn more - LionDesk Texting Suite
            //- Hidden until feature is ready
            a(v-if="false") Registration Logs
</template>

<script>
import TextLimitsWidget from './TextLimitsWidget';
import {
  failedStatuses,
  errorStatuses,
  blockedStatuses,
  soleProprietor,
  getStepByStatusAndType,
  brandSteps,
  businessSteps,
  standard,
  lowStandard,
} from '../config/consoleSteps';
import { createNamespacedHelpers } from 'vuex';
import { REGISTRATION_STATES } from '../config/registrationStatus';
import { LdFeatures } from 'lion-vue';

const { mapState: mapConversationsState, mapActions: mapConversationsActions, mapGetters: mapConversationsGetters } =
  createNamespacedHelpers('conversations');
const { mapState: mapUserInformationState, mapActions: mapUserInformationActions, mapGetters: mapUserInformationGetters } =
  createNamespacedHelpers('userInformation');
export default {
  name: 'TextConsole',
  components: {
    TextLimitsWidget,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    textType: String,
    limit: Boolean,
    remaining: Number,
    statusChecker: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      checker: {},
      currentStep: 0,
      brandSteps: brandSteps,
      businessSteps: businessSteps,
      currentScreenWidth: 0,
    };
  },
  async mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize, { passive: true });
    if (!this.user) {
      await this.getUser();
    }
    if (!this.has10dlcRegister || !this.isCompleted) {
      await this.checkFor10dlcRegister();
    }
    await this.updateStep();
    if (this.statusChecker) {
      this.startChecker();
    }
  },
  beforeDestroy() {
    this.clearChecker();
    window.removeEventListener('resize', this.onResize, { passive: true });
  },
  computed: {
    ...mapConversationsState([
      'has10dlcRegister',
      'registrationStatus10dlc',
      'registrationType10dlc',
      'registrationStatusData10dlc',
      'registrationBrandRetries',
      'registrationCampaignRetries',
    ]),
    ...mapConversationsGetters(['statusData']),
    ...mapUserInformationState(['user']),
    ...mapUserInformationGetters(['hasAccessToFeature']),
    isCompleted() {
      return this.registrationStatus10dlc === 'campaign_completed';
    },
    customNumberPurchasedURL() {
      return this.$route.query.message === 'purchased-custom-number';
    },
    textsLimit() {
      if (!this.isCompleted) {
        return 2000;
      }
      if (
        this.user &&
        this.user.features &&
        this.user.features.individual_texts &&
        this.user.features.individual_texts.has_access
      ) {
        return this.user.features.individual_texts.tracking.limit;
      }
      return 0;
    },
    remainingTexts() {
      if (!this.isCompleted) {
        return 2000;
      }
      if (
        this.user &&
        this.user.features &&
        this.user.features.individual_texts &&
        this.user.features.individual_texts.has_access
      ) {
        return this.user.features.individual_texts.tracking.remaining;
      }
      return 0;
    },
    videoTextsLimit() {
      if (!this.isCompleted) {
        return 500;
      }
      if (
        this.user &&
        this.user.features &&
        this.user.features.video_texts &&
        this.user.features.video_texts.has_access
      ) {
        return this.user.features.video_texts.tracking.limit;
      }
      return 0;
    },
    campaignRetryWarning() {
      return (
        this.registrationCampaignRetries === 0 ? 'NOTE: You are allowed to retry your submission one more time before incurring a new message type registration fee.' :
        this.registrationCampaignRetries >= 1 ? 'NOTE: US mobile carriers will charge you a message type restart fee due to multiple submission attempts.' :
        ''
      );
    },
    remainingVideoTexts() {
      if (!this.isCompleted) {
        return 500;
      }
      if (
        this.user &&
        this.user.features &&
        this.user.features.video_texts &&
        this.user.features.video_texts.has_access
      ) {
        return this.user.features.video_texts.tracking.remaining;
      }
      return 0;
    },
    stepHasError() {
      return (
        failedStatuses.includes(this.registrationStatus10dlc) ||
        errorStatuses.includes(this.registrationStatus10dlc) ||
        this.stepHasBlockedError
      );
    },
    stepHasFailedError() {
      return failedStatuses.includes(this.registrationStatus10dlc) && !this.stepHasTechError && !this.stepHasBlockedError;;
    },
    stepHasTechError() {
      return (
        errorStatuses.includes(this.registrationStatus10dlc) ||
        (this.registrationStatus10dlc == REGISTRATION_STATES.CAMPAIGN_TCR_FAILED &&
          this.registrationCampaignRetries >= 3) ||
        (this.registrationStatus10dlc == REGISTRATION_STATES.CAMPAIGN_TWILIO_FAILED &&
          this.registrationCampaignRetries >= 3)
      );
    },
    stepHasBlockedError() {
      return blockedStatuses.includes(this.registrationStatus10dlc);
    },
    isSolePropeitor() {
      return this.registrationType10dlc === soleProprietor;
    },
    isStandardRegistration() {
      return this.registrationType10dlc === standard;
    },
    isLowStandardRegistration() {
      return this.registrationType10dlc === lowStandard;
    },
    steps() {
      if (!this.isSolePropeitor) {
        return this.brandSteps;
      }
      return this.businessSteps;
    },
    buttonStyle() {
      if (this.stepHasError) {
        return {
          'border-color': 'var(--v-error-base)',
          color: 'var(--v-error-base)',
        };
      }
      return {
        'border-color': 'var(--v-primary-base)',
        color: 'var(--v-primary-base)',
      };
    },
    registrationTypeText() {
      if (this.isSolePropeitor) {
        return 'SOLE PROPRIETOR';
      }

      if (this.isLowStandardRegistration) {
        return 'LOW VOLUME BUSINESS';
      }

      if (this.isStandardRegistration) {
        return 'STANDARD BUSINESS';
      }
    },
    title() {
      if (!this.has10dlcRegister) {
        return 'REGISTER YOUR BUSINESS/BRAND TO UNLOCK TEXTING';
      }
      if (this.isCompleted) {
        return `YOUR <span>${this.registrationTypeText}</span> REGISTRATION IS COMPLETE AND ACTIVE.`;
      }

      return `YOUR <span>${this.registrationTypeText}</span> REGISTRATION IS IN PROGRESS.`;
    },
    subtitle() {
      if (!this.isCompleted) {
        return 'Registration verification is performed by the US mobile carriers.* This process may take up to 2-3 weeks once submitted, depending on the volume of requests. LionDesk is unable to expedite the verification process.';
      }
      return '';
    },
    note() {
      if (!this.isCompleted) {
        // TODO: use the commented line when the 30-day limit on the mobile verification (OTP) process is implemented by twilio/TCR
        return '* Note: Mobile carriers require a one-time activation/vetting fee and a monthly fee for registration maintenance, which we process on your behalf.';
        // return 'Note: Registration must be completed 30 days from initiation. Otherwise, the process will need to be restarted. Mobile carriers require a one-time activation/vetting fee and a monthly fee for registration maintenance, which we process on your behalf.';
      }
      return '';
    },
    errorMessage() {
      if (this.statusData && this.statusData.consoleContent) {
        const { errorMessage } = this.statusData.consoleContent;
        return `: ${errorMessage} `;
      }
      if (this.registrationStatusData10dlc) {
        return `: ${this.registrationStatusData10dlc.map(x => x.description).join(', ')}`;
      }
      return '.';
    },
    afterSupportMessage() {
      if (this.statusData && this.statusData.consoleContent) {
        const { afterSupportMessage } = this.statusData.consoleContent;
        return afterSupportMessage;
      }
      return '';
    },
    errorMailString() {
      let subject = encodeURIComponent('Texting Console: Registration Error');
      let body = encodeURIComponent(
        ''.concat('I am getting this registration error in the Texting Console', this.errorMessage),
      );

      if (this.statusData.errorCase && this.statusData.supportMail) {
        const { subject: customSubject, body: customBody } = this.statusData.supportMail
        subject = encodeURIComponent(customSubject);
        body = encodeURIComponent(customBody);
      }

      return ''.concat('mailto:support@liondesk.com?subject=', subject, '&body=', body);
    },
    shouldComponentsWrap() {
      return this.currentScreenWidth <= 1264;
    },
    limitComponentClasses() {
      const classes = ['lg4', 'xs8'];

      if (!this.shouldComponentsWrap) {
        return ['pr-3', ...classes];
      }
      if (this.isCompleted) {
        return ['order-xs1', 'top-container', ...classes];
      }

      return ['order-xs2', ...classes];
    },
    stateComponentClasses() {
      const classes = ['registration-status-container', 'lg8', 'xs12'];

      if (!this.shouldComponentsWrap) {
        return ['pl-3', ...classes];
      }
      if (this.isCompleted) {
        return ['order-xs2', ...classes];
      }

      return ['top-container', 'order-xs1', ...classes];
    },
    errorActionNote() {
      if (this.registrationStatus10dlc !== REGISTRATION_STATES.BRAND_TCR_FAILED) {
        return ''
      }
      if (this.isStandardRegistration && this.registrationBrandRetries < 2) {
        return 'NOTE: You are allowed to retry your submission a total of 2 times before a new identity registration fee is incurred.'
      }
      if (!this.isStandardRegistration && this.registrationBrandRetries < 3) {
        return 'NOTE: You are allowed to retry your submission a total of 3 times before a new identity registration fee is incurred.'
      }
      return 'NOTE: US mobile carriers will charge you a verification restart fee due to multiple submission attempts.'
    },
    showRetryAttempts() {
      const standardAttempt = [' - First Retry', ' - Final Retry', ' (Requires a restart fee)'];
      const attempt = [' - First Retry', ' - Second Retry', ' - Final Retry', ' (Requires a restart fee)'];
      const campaignSubmissionFailure = ['', ' (Requires a restart fee)']
      if(this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_TCR_FAILED ||
      this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_TWILIO_FAILED
      ) {
        return (this.registrationCampaignRetries <= 1 ? campaignSubmissionFailure[this.registrationCampaignRetries] :
        ' (Requires a restart fee)'
        );
      }
      if (this.registrationStatus10dlc !== REGISTRATION_STATES.BRAND_TCR_FAILED) {
        return '';
      }
      if (this.isStandardRegistration) {
        return standardAttempt[this.registrationBrandRetries];
      }
      return attempt[this.registrationBrandRetries];
    },
    campaignFailure() {
      return (this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_TCR_FAILED ||
      this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_TWILIO_FAILED
      );
    },
    hasBulkTextsAccess() {
      return this.hasAccessToFeature(LdFeatures.BulkTexts);
    },
  },
  methods: {
    ...mapConversationsActions(['checkFor10dlcRegister']),
    ...mapUserInformationActions(['getUser']),
    actionByKeyword(keyword) {
      this.$emit('openRegistrationFlowByKeyword', keyword);
    },
    updateStep() {
      if (this.registrationStatus10dlc && this.registrationType10dlc) {
        this.currentStep = getStepByStatusAndType(
          this.registrationStatus10dlc,
          this.registrationType10dlc,
        );
      }
    },
    async checkStatus() {
      if (!this.isCompleted && this.registrationStatus10dlc) {
        await this.checkFor10dlcRegister(this.registrationType10dlc);
      }
      this.updateStep();
      if (this.isCompleted) {
        this.clearChecker();
      }
    },
    clearChecker() {
      clearInterval(this.checker);
    },
    startChecker() {
      this.checker = setInterval(this.checkStatus, 5000);
    },
    stepState(step) {
      if (step < this.currentStep || this.currentStep === this.steps.length) {
        return 'Completed';
      }
      if (step === this.currentStep && this.stepHasError) {
        return 'Error';
      }
      if (step === this.currentStep) {
        return 'In Progress';
      }
      return 'Pending';
    },
    stepStyle(step) {
      if (step < this.currentStep || this.currentStep === this.steps.length) {
        return {
          color: 'var(--v-success-base)',
        };
      }
      if (step === this.currentStep && this.stepHasError) {
        return {
          color: 'var(--v-error-base)',
        };
      }
      if (step === this.currentStep) {
        return {
          color: 'var(--v-primary-base)',
        };
      }
      return 'Pending';
    },
    completeIcon(step) {
      if (step < this.currentStep || this.currentStep === this.steps.length) {
        return 'check';
      }
      return '';
    },
    stepColor(step) {
      if (step < this.currentStep || this.currentStep === this.steps.length) {
        return 'success';
      }
      if (step === this.currentStep && this.stepHasError) {
        return 'error';
      }
      if (step === this.currentStep) {
        return 'primary';
      }
    },
    onResize() {
      this.currentScreenWidth = window.innerWidth;
    },
  },
  watch: {
    statusChecker(newValue) {
      if (newValue) {
        this.startChecker();
        return
      }

      if (this.checker && !newValue) {
        this.clearChecker();
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.headline /deep/ span {
  color: var(--v-primary-base);
}

.colapse {
  @media screen and (max-width: 1600px) {
    width: min-content;
    white-space: break-spaces;
  }
}

.console-card {
  display: flex;
  flex-direction: row;
  padding: 32px;
}

.top-container {
  padding-bottom: 30px !important;
}

.registration-status-container {
  flex-direction: column;
  justify-content: space-between;
}

.text-component {
  white-space: pre-line;
}

.logo {
  width: 70%;
  height: 70%;
}

.content {
  padding: 0px;
}

.stepper-header {
  flex-wrap: nowrap;
  border: none;
  box-shadow: none;
}

.stepper-container {
  border: none;
  box-shadow: none;
  padding: 0;
  margin: 0;
}

.content-button {
  border: solid 2px !important;
  background-color: transparent !important;
  text-transform: none;
}

.step {
  display: flex;
  align-items: flex-start;
}

.divider {
  margin: 35px -16px 0px -120px !important;
}

.error-message {
  color: var(--v-error-base);
}

.purcharsed-custom-number {
  border: 1px solid var(--v-success-darken2);
  border-radius: 5px;

  &--main {
    color: var(--v-success-darken2);
  }

  &--regular {
    color: var(--v-text-base);
  }
}

.campaign-warning {
  color: var(--v-error-base);
}
</style>
